import { inject, Injectable } from '@angular/core';
import { Browser } from './browser';

@Injectable({
  providedIn: 'root',
})
export class Storage {
  private readonly browser = inject(Browser);

  /**
   * Función que valida si existe o no una llave en localStorage
   * @param key Recibe el nombre de la llave
   * @returns Devuelve verdadero si la llave existe, sino devuelve false.
   */
  isLocal(key: string): boolean {
    if (!this.browser.isBrowser) return false;
    return !!localStorage.getItem(key);
  }

  /**
   * Función que obtiene el valor de una llave en localStorage
   * @param key Recibe el nombre de la llave
   * @returns Devuelve el valor actual asociado a la llave
   */
  getLocal = (key: string): any => {
    if (!this.browser.isBrowser) return null;

    const item = localStorage.getItem(key);
    if (!item) return null;

    try {
      return JSON.parse(item);
    } catch (error) {
      return item;
    }
  };

  /**
   * Función que asigna un valor a una llave en localStorage
   * @param key Recibe el nombre de la llave
   * @param value Recibe el valor para asigna a la llave
   */
  setLocal = (key: string, value: any): void => {
    if (!this.browser.isBrowser) return;
    localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  };

  /**
   * Función que limpia todo localStorage
   */
  clearLocal(): void {
    if (!this.browser.isBrowser) return;
    localStorage.clear();
  }

  /**
   * función que elimina una llave de localStorage
   * @param key Recibe el nombre de la llave
   */
  removeLocal(key: string): void {
    if (!this.browser.isBrowser) return;
    localStorage.removeItem(key);
  }

  /**
   * Función que valida si existe o no una llave en sessionStorage
   * @param key Recibe el nombre de la llave
   * @returns Devuelve verdadero si la llave existe, sino devuelve false.
   */
  isSession(key: string): boolean {
    if (!this.browser.isBrowser) return false;
    return !!sessionStorage.getItem(key);
  }

  /**
   * Función que obtiene el valor de una llave en sessionStorage
   * @param key Recibe el nombre de la llave
   * @returns Devuelve el valor actual asociado a la llave
   */
  getSession = (key: string): any => {
    if (!this.browser.isBrowser) return null;

    const item = sessionStorage.getItem(key);
    if (!item) return null;

    try {
      return JSON.parse(item);
    } catch (error) {
      return item;
    }
  };

  /**
   * Función que asigna un valor a una llave en sessionStorage
   * @param key Recibe el nombre de la llave
   * @param value Recibe el valor para asigna a la llave
   */
  setSession = (key: string, value: any): void => {
    if (!this.browser.isBrowser) return;
    sessionStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  };

  /**
   * Función que limpia todo sessionStorage
   */
  clearSession(): void {
    if (!this.browser.isBrowser) return;
    sessionStorage.clear();
  }

  /**
   * función que elimina una llave de sessionStorage
   * @param key Recibe el nombre de la llave
   */
  removeSession(key: string): void {
    if (!this.browser.isBrowser) return;
    sessionStorage.removeItem(key);
  }
}
